import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Crown, Sparkles, Infinity as InfinityIcon, Palette, BarChart3, Brain, Users, MessageCircle, Hourglass } from "lucide-react";
import { useAccess } from "../context/AccessContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { trackEvent, EVENTS } from "../lib/analytics";

// Reminder popup controller. Decides at most ONE popup per app session/day:
//   - "expiring_soon": trial or paid premium has 1 or 2 days left
//                      (Jun 2026 — universal pre-expiry nudge)
//   - "trial_ended":   welcome window has expired, user is now on Free
//   - "renewal_soon":  paid premium expiring (3d / 7d for monthly / yearly)
//
// Architecture note (Jun 2026): the open state is DERIVED from access +
// dismissed-this-session flag — no setState inside useEffect. This keeps the
// React Compiler's `set-state-in-effect` rule happy and removes the entire
// class of "popup re-opens itself in a loop" bugs.
//
// Cooldown rules:
//   - "Maybe later" suppresses the same kind for 24 h via localStorage stamp
//   - At most 1 popup of any kind per 24 h (global localStorage stamp)
//   - For "expiring_soon", backend `expiry_warning_today_shown` is also
//     consulted so dismissal persists across devices/PWA installs.
//   - No popup on /premium itself, on auth pages, or before access loads

const COPY = {
  expiring_soon: {
    title: "⏳ Premium Access Ending Soon",
    description: "Your Premium Access will expire soon. Upgrade now to continue enjoying:",
    primary: "Upgrade Now 🚀",
    secondary: "Maybe Later",
  },
  trial_ended: {
    title: "Your Syllox Premium experience has ended 🚀",
    description: "Continue your productivity journey with Syllox Premium.",
    primary: "Upgrade Premium",
    secondary: "Maybe later",
  },
  renewal_soon: {
    title: "Your Syllox Premium expires soon 🚀",
    description: "Renew Premium to continue your productivity journey.",
    primary: "Renew Premium",
    secondary: "Maybe later",
  },
};

// Spec PART 4 perk list — used by the expiring_soon popup.
const EXPIRING_PERKS = [
  { icon: InfinityIcon,  label: "Unlimited Subjects" },
  { icon: InfinityIcon,  label: "Unlimited Chapters" },
  { icon: Brain,         label: "AI Doubt Solving" },
  { icon: BarChart3,     label: "Mock Test Analytics" },
  { icon: Users,         label: "Community Features" },
  { icon: MessageCircle, label: "Friends & Messaging" },
  { icon: BarChart3,     label: "Advanced Reports" },
];

const LEGACY_PERKS = [
  { icon: InfinityIcon, label: "Unlimited chapters" },
  { icon: Sparkles, label: "Today Targets" },
  { icon: Palette, label: "Premium themes" },
  { icon: BarChart3, label: "Advanced productivity tools" },
];

const KIND_KEY = (k) => `syllox_reminder_${k}_dismissed_at`;
const GLOBAL_KEY = "syllox_reminder_shown_at";
const DAY_MS = 24 * 60 * 60 * 1000;
const ROUTE_BLOCKLIST = ["/premium", "/login", "/auth/phone", "/auth/google/callback"];

function decideKind(access) {
  if (!access) return null;

  // Universal pre-expiry nudge (trial OR paid premium, 1-2 days left).
  if (access.in_trial && (access.trial_days_left === 1 || access.trial_days_left === 2)) {
    return "expiring_soon";
  }
  if (access.is_premium && access.premium_days_left != null) {
    if (access.premium_days_left === 1 || access.premium_days_left === 2) {
      return "expiring_soon";
    }
    const planKind = access.premium_plan_kind;
    const renewalThresholds = planKind === "yearly" ? [7, 3] : [3];
    if (renewalThresholds.includes(access.premium_days_left)) return "renewal_soon";
    return null;
  }

  // Trial ended, user is now Free.
  if (!access.in_trial && !access.is_premium && access.trial_ends_at) {
    const trialEnded = new Date(access.trial_ends_at).getTime();
    if (trialEnded > 0 && trialEnded < Date.now()) return "trial_ended";
  }
  return null;
}

function withinCooldown(kind, access) {
  if (kind === "expiring_soon" && access?.expiry_warning_today_shown) return true;
  const last = parseInt(localStorage.getItem(KIND_KEY(kind)) || "0", 10);
  if (last && Date.now() - last < DAY_MS) return true;
  const lastAny = parseInt(localStorage.getItem(GLOBAL_KEY) || "0", 10);
  if (lastAny && Date.now() - lastAny < DAY_MS) return true;
  return false;
}

export default function PremiumReminderController() {
  const { user } = useAuth();
  const { access, refresh } = useAccess();
  const location = useLocation();
  const navigate = useNavigate();

  // Local "dismissed this session" flag. Persists across renders inside the
  // current page session; localStorage takes over for cross-session memory.
  const [dismissedKind, setDismissedKind] = useState(null);

  const kind = useMemo(() => decideKind(access), [access]);

  // Derived flag — no setState involved, no effect-set-state loop risk.
  const shouldShow = useMemo(() => {
    if (!user || !access || !kind) return false;
    if (dismissedKind === kind) return false;
    if (access.founders_reward_pending) return false;
    if (ROUTE_BLOCKLIST.some((r) => location.pathname.startsWith(r))) return false;
    if (withinCooldown(kind, access)) return false;
    return true;
  }, [user, access, kind, dismissedKind, location.pathname]);

  // Stamp localStorage + analytics on the very first render where the
  // popup goes open. The effect ONLY runs side effects — no setState — so
  // the React Compiler is happy.
  useEffect(() => {
    if (!shouldShow) return;
    localStorage.setItem(GLOBAL_KEY, String(Date.now()));
    if (kind === "expiring_soon") {
      trackEvent(EVENTS.PREMIUM_EXPIRY_WARNING_SHOWN, {
        days_left: access?.in_trial ? access.trial_days_left : access?.premium_days_left,
        source: access?.in_trial ? "trial" : "premium",
      });
    }
  }, [shouldShow, kind, access?.in_trial, access?.trial_days_left, access?.premium_days_left]);

  const persistDismiss = async () => {
    if (kind) localStorage.setItem(KIND_KEY(kind), String(Date.now()));
    if (kind === "expiring_soon") {
      try {
        await api.post("/premium-reminder/dismiss");
        if (refresh) refresh();
      } catch { /* see top-of-file comment */ }
    }
  };

  const onClose = () => {
    if (kind === "expiring_soon") {
      trackEvent(EVENTS.PREMIUM_EXPIRY_WARNING_DISMISSED);
    }
    setDismissedKind(kind);
    persistDismiss();
  };

  const onPrimary = () => {
    if (kind === "expiring_soon") {
      trackEvent(EVENTS.PREMIUM_UPGRADE_CLICKED, { source: "expiry_warning" });
    }
    setDismissedKind(kind);
    persistDismiss();
    navigate("/premium");
  };

  if (!kind) return null;
  const copy = COPY[kind];
  const perks = kind === "expiring_soon" ? EXPIRING_PERKS : LEGACY_PERKS;
  const isExpiring = kind === "expiring_soon";

  return (
    <Dialog open={shouldShow} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent
        className="max-w-md gap-0 overflow-hidden rounded-3xl border-emerald-500/30 bg-white p-0 shadow-2xl shadow-emerald-500/10 dark:bg-zinc-950"
        data-testid={`reminder-modal-${kind}`}
      >
        <div className={`relative overflow-hidden px-6 py-7 text-white ${isExpiring ? "bg-gradient-to-br from-amber-500 to-orange-500" : "bg-gradient-to-br from-emerald-500 to-emerald-600"}`}>
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" aria-hidden="true"/>
          <div className="relative flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/15 backdrop-blur-sm">
              {isExpiring ? <Hourglass size={22}/> : <Crown size={22}/>}
            </div>
            <div>
              <DialogTitle className="font-heading text-xl font-bold text-white">
                {copy.title}
              </DialogTitle>
              <p className={`text-xs ${isExpiring ? "text-amber-50/90" : "text-emerald-50/90"}`}>Syllox Premium</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          <DialogDescription className="text-sm text-zinc-600 dark:text-zinc-400">
            {copy.description}
          </DialogDescription>
          <ul className="mt-5 space-y-2.5 text-sm">
            {perks.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2.5 text-zinc-700 dark:text-zinc-300">
                <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full ${isExpiring ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400" : ""}`}>
                  {isExpiring ? (
                    <svg viewBox="0 0 20 20" className="h-3 w-3 fill-current" aria-hidden="true">
                      <path d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.4 7.4a1 1 0 0 1-1.4 0L3.3 9.5A1 1 0 1 1 4.7 8L9 12.3l6.3-6.3a1 1 0 0 1 1.4 0Z"/>
                    </svg>
                  ) : (
                    <Icon size={16} className="text-emerald-500"/>
                  )}
                </span>
                <span>{label}</span>
              </li>
            ))}
          </ul>
          <div className="mt-7 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              data-testid="reminder-modal-maybe-later"
              className="text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              {copy.secondary}
            </Button>
            <Button
              type="button"
              onClick={onPrimary}
              data-testid="reminder-modal-primary"
              className={`px-5 text-white shadow-md ${isExpiring ? "bg-gradient-to-r from-amber-500 to-orange-500 shadow-amber-500/30 hover:from-amber-600 hover:to-orange-600" : "bg-gradient-to-r from-emerald-500 to-emerald-600 shadow-emerald-500/30 hover:from-emerald-600 hover:to-emerald-700"}`}
            >
              <Sparkles size={14} className="mr-1.5"/>
              {copy.primary}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
